import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Mail, Phone, ArrowLeft, ShieldCheck, EyeOff } from "lucide-react";
import { getProposalAdminPreview } from "@/lib/proposals.functions";

export const Route = createFileRoute("/_authenticated/admin/proposals/$id/preview")({
  head: () => ({ meta: [{ title: "Admin preview — Proposal" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: AdminPreview,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="py-20 text-center">
        <p className="text-red-400 mb-3">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
      </div>
    );
  },
});

const fmt = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Number(n || 0));

function AdminPreview() {
  const { id } = Route.useParams();
  const fn = useServerFn(getProposalAdminPreview);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "proposal-preview", id],
    queryFn: () => fn({ data: { id } }),
  });

  if (isLoading || !data) {
    return <div className="py-16 grid place-items-center"><Loader2 className="size-5 animate-spin text-zinc-500" /></div>;
  }

  const p = data.proposal as any;
  const items = data.line_items as any[];
  const colors = data.colors as any[];
  const company = data.company || ({} as any);
  const subtotal = items.reduce((s, i) => s + Number(i.total || 0), 0);
  const total = Number(p.total || subtotal);

  return (
    <div className="-m-6 lg:-m-10 min-h-screen bg-[#fafaf7] text-zinc-900">
      {/* Admin banner */}
      <div className="sticky top-0 z-30 bg-amber-300 border-b border-amber-500 text-zinc-950 text-xs flex items-center justify-between px-4 py-2">
        <span className="inline-flex items-center gap-2 font-semibold">
          <EyeOff className="size-3.5" /> ADMIN PREVIEW — this is what the customer will see. Status:
          <span className="uppercase tracking-wider bg-zinc-950 text-amber-300 px-2 py-0.5 rounded">{p.status.replace("_"," ")}</span>
        </span>
        <Link to="/admin/proposals/$id" params={{ id }} className="inline-flex items-center gap-1 font-semibold hover:underline">
          <ArrowLeft className="size-3" /> Back to editor
        </Link>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-8 lg:py-12">
        <header className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-6 lg:p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-amber-700 text-[11px] tracking-[0.25em] uppercase font-semibold">Painting Proposal</p>
              <h1 className="font-serif text-3xl lg:text-4xl mt-2 text-zinc-900">{company.name || "Michael's Painting"}</h1>
              <div className="mt-3 text-xs text-zinc-400 space-y-0.5">
                {company.phone && <p><Phone className="inline size-3 mr-1" />{company.phone}</p>}
                {company.email && <p><Mail className="inline size-3 mr-1" />{company.email}</p>}
                {company.website && <p>{company.website}</p>}
              </div>
            </div>
            <div className="text-right text-xs text-zinc-500">
              <p>Proposal #</p>
              <p className="font-mono text-amber-700 text-base">{p.proposal_number}</p>
              <p className="mt-2">Date: {new Date(p.created_at).toLocaleDateString()}</p>
              {p.expires_at && <p>Expires: {new Date(p.expires_at).toLocaleDateString()}</p>}
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-zinc-200 grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Prepared for</p>
              <p className="font-medium">{p.customer_name}</p>
              {p.customer_email && <p className="text-zinc-400 text-xs">{p.customer_email}</p>}
              {p.customer_phone && <p className="text-zinc-400 text-xs">{p.customer_phone}</p>}
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Property</p>
              <p className="text-zinc-700">{p.property_address || "—"}</p>
              <p className="text-zinc-500 text-xs mt-1 capitalize">{p.project_type}{p.estimated_timeline ? ` · ${p.estimated_timeline}` : ""}</p>
            </div>
          </div>
        </header>

        {Array.isArray(p.scope) && p.scope.length > 0 && (
          <Section title="Scope of work">
            <ul className="space-y-1.5 text-sm text-zinc-700">
              {p.scope.map((s: string, i: number) => <li key={i} className="flex gap-2"><span className="text-amber-700 mt-1">•</span><span>{s}</span></li>)}
            </ul>
          </Section>
        )}

        {colors.length > 0 && (
          <Section title="Paint schedule">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-zinc-500">
                <tr><th className="text-left py-2">Surface</th><th className="text-left">Color</th><th className="text-left">SW #</th><th className="text-left">Sheen</th><th className="text-left">Coats</th></tr>
              </thead>
              <tbody>
                {colors.map((c, i) => (
                  <tr key={i} className="border-t border-zinc-100">
                    <td className="py-2 text-zinc-700">{c.surface}</td>
                    <td className="py-2"><span className="inline-flex items-center gap-2"><span className="inline-block size-4 rounded border border-zinc-300" style={{ background: c.hex || "#fff" }} />{c.color_name || "—"}</span></td>
                    <td className="py-2 text-zinc-500 font-mono text-xs">{c.color_number || "—"}</td>
                    <td className="py-2 text-zinc-400">{c.sheen || "—"}</td>
                    <td className="py-2 text-zinc-400">{c.coats}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        <Section title="Investment">
          <table className="w-full text-sm">
            <tbody>
              {items.map((i, idx) => (
                <tr key={idx} className="border-b border-zinc-100">
                  <td className="py-2.5">
                    <p className="text-zinc-900">{i.title}</p>
                    {i.description && <p className="text-zinc-500 text-xs">{i.description}</p>}
                  </td>
                  <td className="py-2.5 text-right tabular-nums text-zinc-700">{fmt(Number(i.total))}</td>
                </tr>
              ))}
              <tr>
                <td className="pt-3 font-semibold text-zinc-900">Total</td>
                <td className="pt-3 text-right font-serif text-2xl text-amber-700">{fmt(total)}</td>
              </tr>
            </tbody>
          </table>
        </Section>

        {p.warranty && <Section title="Warranty"><p className="text-sm text-zinc-700 whitespace-pre-wrap">{p.warranty}</p></Section>}
        {p.terms && <Section title="Terms & conditions"><p className="text-xs text-zinc-400 whitespace-pre-wrap leading-relaxed">{p.terms}</p></Section>}

        <footer className="mt-8 flex items-center justify-between text-xs text-zinc-500">
          <span className="inline-flex items-center gap-1"><ShieldCheck className="size-3.5" /> Admin-only preview — not accessible via public link.</span>
        </footer>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-4 bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm">
      <h2 className="text-[11px] uppercase tracking-[0.2em] text-amber-700 font-semibold mb-3">{title}</h2>
      {children}
    </section>
  );
}